import { useCallback, useEffect, useState } from 'react';
import {
  MessageCircle, Send, Clock, CheckCircle, AlertCircle, Loader2, ArrowLeft, X,
} from 'lucide-react';
import {
  listMyTickets, createTicket, getTicket, replyTicket,
  type TicketSummary, type TicketDetail, type TicketStatus,
} from '@/shared/services/ticketsService';
import { useToast } from '@/shared/contexts/ToastContext';

const STATUS_MAP: Record<string, { label: string; color: string; icon: typeof Clock }> = {
  open:     { label: 'Aberto',     color: 'text-yellow-400', icon: Clock },
  answered: { label: 'Respondido', color: 'text-[#8fb9de]',  icon: CheckCircle },
  closed:   { label: 'Fechado',    color: 'text-white/55',   icon: AlertCircle },
};

export default function SupportPage() {
  const { notify } = useToast();
  const [items, setItems] = useState<TicketSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<TicketDetail | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try { setItems(await listMyTickets()); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { fetchList(); }, [fetchList]);

  if (selected) {
    return (
      <TicketView
        ticket={selected}
        onBack={() => { setSelected(null); fetchList(); }}
        onUpdate={setSelected}
      />
    );
  }

  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-bold text-white mb-6">Suporte</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* New ticket */}
        <NewTicketForm onCreated={(t) => { fetchList(); setSelected(t); }} />

        {/* My tickets */}
        <div className="surface-card rounded-md p-6">
          <div className="flex items-center gap-2 mb-5">
            <Clock size={18} className="text-[#8fb9de]" />
            <h2 className="text-lg font-bold text-white">Seus Tickets</h2>
            <span className="ml-auto text-xs text-white/65">{items.length} total</span>
          </div>

          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div>
          ) : items.length === 0 ? (
            <p className="text-center text-white/40 py-8 text-sm">
              Você ainda não abriu nenhum ticket.
            </p>
          ) : (
            <div className="space-y-0">
              {items.map((t, i) => {
                const s = STATUS_MAP[t.status] || STATUS_MAP.open;
                const Icon = s.icon;
                return (
                  <button
                    key={t.id}
                    onClick={async () => {
                      try { setSelected(await getTicket(t.id)); }
                      catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
                    }}
                    className={`w-full flex items-center gap-3 py-3 px-2 cursor-pointer
                                hover:bg-white/5 rounded-md transition-all text-left
                                ${i < items.length - 1 ? 'border-b border-white/5' : ''}`}
                  >
                    <Icon size={16} className={s.color} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">
                        #{t.id} — {t.subject}
                      </p>
                      <p className="text-xs text-white/55 mt-0.5">
                        {new Date(t.updatedAt).toLocaleString('pt-BR')}
                        {' · '}{t.messageCount} mensagem{t.messageCount !== 1 ? 's' : ''}
                        {t.lastReplyByStaff && t.status === 'answered' && (
                          <span className="ml-1 text-[#8fb9de]">· Resposta nova</span>
                        )}
                      </p>
                    </div>
                    <span className={`text-xs font-medium ${s.color}`}>{s.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ----------------------------------------------------------------- New ticket

function NewTicketForm({ onCreated }: { onCreated: (t: TicketDetail) => void }) {
  const { notify } = useToast();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (subject.trim().length < 3 || body.trim().length < 3) {
      notify('error', 'Preencha assunto e mensagem (mínimo 3 caracteres).');
      return;
    }
    setBusy(true);
    try {
      const t = await createTicket({ subject: subject.trim(), body: body.trim() });
      setSubject(''); setBody('');
      notify('success', 'Ticket aberto!');
      onCreated(t);
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface-card rounded-md p-6">
      <div className="flex items-center gap-2 mb-5">
        <MessageCircle size={18} className="text-[#8fb9de]" />
        <h2 className="text-lg font-bold text-white">Novo Ticket</h2>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-xs text-white/65 mb-1.5">Assunto</label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Descreva o problema brevemente"
            maxLength={120}
            className="w-full px-4 py-2.5 rounded-md text-sm bg-white/5 border border-white/10
                       focus:border-[#8fb9de] placeholder:text-white/30"
          />
        </div>
        <div>
          <label className="block text-xs text-white/65 mb-1.5">Mensagem</label>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Descreva o problema em detalhes..."
            rows={5}
            maxLength={4000}
            className="w-full px-4 py-2.5 rounded-md text-sm bg-white/5 border border-white/10
                       focus:border-[#8fb9de] resize-none placeholder:text-white/30"
          />
          <p className="text-[10px] text-white/35 mt-1 text-right">{body.length}/4000</p>
        </div>
        <button type="submit" disabled={busy}
                className="btn-primary px-6 py-2.5 rounded-md text-sm font-semibold
                           flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Enviar Ticket
        </button>
      </form>
    </div>
  );
}


// ----------------------------------------------------------------- View ticket

function TicketView({
  ticket, onBack, onUpdate,
}: {
  ticket: TicketDetail;
  onBack: () => void;
  onUpdate: (t: TicketDetail) => void;
}) {
  const { notify } = useToast();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const status = STATUS_MAP[ticket.status] || STATUS_MAP.open;
  const StatusIcon = status.icon;

  const reply = async () => {
    if (body.trim().length < 1) return;
    setBusy(true);
    try {
      const next = await replyTicket(ticket.id, { body: body.trim() });
      setBody('');
      onUpdate(next);
      notify('success', 'Mensagem enviada');
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <button onClick={onBack}
        className="text-sm text-white/65 hover:text-white flex items-center gap-2 mb-4 cursor-pointer">
        <ArrowLeft size={14} /> Voltar para meus tickets
      </button>

      <div className="surface-card rounded-md p-6 mb-4">
        <div className="flex items-start justify-between mb-2">
          <h1 className="text-xl md:text-2xl font-bold text-white">
            #{ticket.id} — {ticket.subject}
          </h1>
          <span className={`flex items-center gap-1 text-sm font-medium ${status.color}`}>
            <StatusIcon size={16} /> {status.label}
          </span>
        </div>
        <p className="text-xs text-white/55">
          Aberto em {new Date(ticket.createdAt).toLocaleString('pt-BR')}
          {' · '}última atualização {new Date(ticket.updatedAt).toLocaleString('pt-BR')}
        </p>
      </div>

      <div className="surface-card rounded-md p-6 mb-4">
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          {ticket.messages.map((m) => (
            <div key={m.id}
                 className={`rounded-md p-4 ${m.isStaff
                   ? 'bg-[#8fb9de]/[0.06] border border-[#8fb9de]/30'
                   : 'bg-white/[0.04] border border-white/[0.06]'}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`text-[11px] font-bold uppercase tracking-wider
                                  ${m.isStaff ? 'text-[#8fb9de]' : 'text-white/85'}`}>
                  {m.isStaff ? 'SUPORTE' : 'VOCÊ'}
                  {m.authorId && ` · ${m.authorId}`}
                </span>
                <span className="text-[10px] text-white/45 ml-auto">
                  {new Date(m.createdAt).toLocaleString('pt-BR')}
                </span>
              </div>
              <p className="text-sm text-white/85 whitespace-pre-wrap break-words">{m.body}</p>
            </div>
          ))}
        </div>
      </div>

      {ticket.status === 'closed' ? (
        <div className="surface-card rounded-md p-4 border-white/[0.04]">
          <p className="text-sm text-white/55 text-center">
            Este ticket foi fechado. Se precisar de mais ajuda, abra um novo.
          </p>
        </div>
      ) : (
        <div className="surface-card rounded-md p-6">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle size={16} className="text-[#8fb9de]" />
            <h3 className="text-sm font-bold text-white/85">Responder</h3>
          </div>
          <textarea
            value={body} onChange={(e) => setBody(e.target.value)}
            rows={4} maxLength={4000}
            placeholder="Escreva sua mensagem..."
            className="w-full px-4 py-2.5 rounded-md text-sm bg-white/5 border border-white/10
                       focus:border-[#8fb9de] resize-none placeholder:text-white/30"
          />
          <div className="flex justify-end mt-3">
            <button onClick={reply} disabled={busy || body.trim().length < 1}
              className="btn-primary px-6 py-2.5 rounded-md text-sm font-semibold
                         flex items-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Enviar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
